import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";

interface AlertCardProps {
  accent: string;
  icon: React.ReactNode;
  title: string;
  badge: string;
  detail: string;
  flag: string;
  time: string;
  unread?: boolean;
  selected?: boolean;
  disclaimer?: string;
  metadata?: Array<{ label: string; value: string }>;
  action?: { label: string; href: string };
  onClick?: () => void;
}

const AlertCard: React.FC<AlertCardProps> = ({
  accent,
  icon,
  title,
  badge,
  detail,
  flag,
  time,
  unread = false,
  selected = false,
  disclaimer,
  metadata = [],
  action,
  onClick,
}) => (
  <Box
    sx={{
      width: "100%",
      p: 2,
      borderRadius: "16px",
      bgcolor: selected ? `${accent}12` : "rgba(2, 6, 23, 0.9)",
      color: "inherit",
      textAlign: "left",
      font: "inherit",
      border: `1px solid ${selected ? `${accent}CC` : `${accent}4D`}`,
      position: "relative",
      overflow: "hidden",
      transition: "border-color 0.2s, background-color 0.2s",
      "&:hover": { borderColor: `${accent}99` },
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        width: unread ? 6 : 3,
        height: "100%",
        bgcolor: accent,
      },
    }}
  >
    <Box
      component={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        width: "100%",
        p: 0,
        bgcolor: "transparent",
        color: "inherit",
        textAlign: "left",
        font: "inherit",
        border: 0,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <Stack direction="row" spacing={1.75} alignItems="flex-start">
      <Box
        sx={{
          p: 1.25,
          borderRadius: "12px",
          bgcolor: `${accent}1A`,
          color: accent,
          display: "flex",
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography noWrap sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#fff" }}>
            {title}
          </Typography>
          <Chip
            size="small"
            label={badge}
            sx={{
              height: 20,
              fontSize: "0.6rem",
              fontFamily: "monospace",
              bgcolor: `${accent}33`,
              color: accent,
            }}
          />
        </Stack>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "#94a3b8",
            mt: 0.5,
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {detail}
        </Typography>
        {disclaimer && (
          <Tooltip title={disclaimer} arrow>
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="flex-start"
              sx={{
                mt: 1,
                p: 0.75,
                borderRadius: 1.5,
                bgcolor: `${accent}12`,
                color: "#cbd5e1",
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15, color: accent, mt: "1px" }} />
              <Typography sx={{ fontSize: "0.65rem", lineHeight: 1.4 }}>
                Airbnb may show an approximate location. Review this nearby
                listing before taking action.
              </Typography>
            </Stack>
          </Tooltip>
        )}
        {metadata.length > 0 && (
          <Box
            sx={{
              mt: 1.25,
              pt: 1.25,
              borderTop: "1px solid rgba(148, 163, 184, 0.16)",
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 1,
            }}
          >
            {metadata.map((item) => (
              <Box key={item.label} minWidth={0}>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  title={item.value}
                  noWrap
                  sx={{ color: "#e2e8f0", fontSize: "0.68rem", mt: 0.15 }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.25, fontSize: "0.7rem", color: "#94a3b8" }}
        >
          <Box component="span" sx={{ color: accent, fontWeight: 500 }}>
            {flag}
          </Box>
          <Box component="span" sx={{ fontFamily: "monospace", flexShrink: 0 }}>
            {time}
          </Box>
        </Stack>
      </Box>
      </Stack>
    </Box>
    {action && (
      <Button
        component="a"
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        size="small"
        fullWidth
        disableRipple
        onClick={(event) => event.stopPropagation()}
        sx={{
          mt: 1.5,
          borderRadius: 2,
          bgcolor: accent,
          "&:hover": { bgcolor: accent, filter: "brightness(1.08)" },
        }}
      >
        {action.label}
      </Button>
    )}
  </Box>
);

export default AlertCard;
